// src/pages/ModuleAssignment.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import RoleCreationModal from "./RoleCreationModal";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import { useQueryClient } from "@tanstack/react-query";
import hub from "../services/EventHub";

/* Ant Design v6 */
import {
  App,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Empty,
  Flex,
  Grid,
  Input,
  Modal,
  Segmented,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tooltip,
  Typography,
} from "antd";
import {
  InfoCircleOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  SaveOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  ApartmentOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/* --- Friendly labels (unchanged) --------------------------------------- */
const menuLabelMap = {
  AboutUsPage: "About Us",
  AdminRegistrationPage: "Admin Registration",
  AmenitiesPage: "Amenities",
  AmenitiesListPage: "Amenities List",

  LandingPage: "Home",
  Login: "Login",
  Registration: "Register",
  ResetPassword: "Reset Password",
  ForgotPassword: "Forgot Password",
  Unauthorized: "Unauthorized",
  EmailConfirmation: "Confirm Email",
  ResendConfirmationPage: "Resend Confirmation",
  EmailConfirmationPendingPage: "Email Confirmation Pending",

  Dashboard: "Dashboard",
  AdminDashboard: "Admin Dashboard",
  Settings: "Settings",
  SettingsPage: "Settings Page",
  ProfileSettingsPage: "Profile Settings",
  AccountSettingsPage: "Account Settings",

  Hotel: "Hotel",
  HotelDashboardCards: "Hotel Dashboard Cards",
  HotelEditModal: "Edit Hotel",
  HotelForm: "Hotel Form",
  HotelFormStep1A: "Hotel Form Step 1A",
  HotelFormStep1AA: "Hotel Form Step 1AA",
  HotelFormStep1B: "Hotel Form Step 1B",
  HotelFormStep2: "Hotel Form Step 2",
  HotelFormStep3: "Hotel Form Step 3",
  HotelFormStep5Registration: "Hotel Registration",
  HotelList: "Hotel List",
  HotelProfile: "Hotel Profile",
  HotelProfileHeader: "Hotel Profile Header",
  HotelProfilePage: "Hotel Profile",
  Hotels: "Hotels",
  BookingListPage: "Booking List",
  BookingModal: "Booking Modal",
  BookingPage: "Booking",
  BookingTypesPage: "Booking Types",
  Breadcrumb: "Breadcrumb",
  Calendar: "Calendar",
  ChangeUserRoleModal: "Change User Role",
  ChatSidebar: "Chat Sidebar",
  ChatWindow: "Chat Window",
  CheckInOutPage: "Check In/Out",
  ChecklistModal: "Checklist",
  CheckOut: "Check Out",
  CompleteUserProfile: "Complete Profile",
  ContactUsPage: "Contact Us",
  countries: "Countries",
  CreateServicePage: "Create Service",
  CreateTaskModal: "Create Task",
  DeleteConfirmationModal: "Delete Confirmation",
  EditableField: "Editable Field",
  EditBookingModal: "Edit Booking",
  EditGuestBillModal: "Edit Guest Bill",
  EditGuestModal: "Edit Guest",
  EditOrderModal: "Edit Order",
  EditRoomModal: "Edit Room",
  EditUserModal: "Edit User",
  EstablishmentSetupPage: "Establishment Setup",
  EventsList: "Events List",
  EventsListPage: "Events List",
  EventsPage: "Events",
  FacilitiesPage: "Facilities",
  FeaturesPage: "Features",
  FinancialReportsPage: "Financial Reports",
  FloorPlansPage: "Floor Plans",
  GuestBillingPage: "Guest Billing",
  GuestListPage: "Guest List",
  GuestProfilesPage: "Guest Profiles",
  HousekeeperDashboard: "Housekeeper Dashboard",
  HousekeepingDashboard: "Housekeeping Dashboard",
  HousekeepingModal: "Housekeeping Modal",
  HousekeepingPage: "Housekeeping",
  HousekeepingReportsPage: "Housekeeping Reports",
  KycApprovalPage: "KYC Approval",
  KycApprove: "KYC Approve",
  KycReject: "KYC Reject",
  KYCRejectionPage: "KYC Rejection",
  KycReviewPage: "KYC Review",
  KYCUploadPage: "KYC Upload",
  KYCVerificationPage: "KYC Verification",
  MaintenanceDashboard: "Maintenance Dashboard",
  MaintenancePage: "Maintenance Task",
  MaintenanceRequestForm: "Request Maintenance",
  ManageStaffPage: "Manage Staff",
  ManageUsers: "Manage Users",
  MinimalBookingForm: "Minimal Booking Form",
  Modal: "Modal",
  ModuleAssignment: "Module Assignment",
  NotFound: "Not Found",
  PricingPage: "Pricing",
  ProfileSettings: "Profile Settings",
  ProgressBar: "Progress Bar",
  PromotionsPage: "Promotions",
  RatesPage: "Rates",
  RejectKyc: "Reject KYC",
  ReportsPage: "Reports",
  RequestResetPassword: "Request Reset Password",
  RequestServiceModal: "Request Service",
  ReservatioModal: "Reservatio Modal",
  ReservationCalendar: "Reservation Calendar",
  ReservationCalendarPage: "Reservation Calendar",
  ReservationGantt: "Reservation Gantt",
  ReservationGanttWrapper: "Reservation Gantt Wrapper",
  ReservationReportsPage: "Reservation Reports",
  RestaurantOrderPage: "Restaurant Orders",
  RoleCreationModal: "Role Creation",
  RoomBookingCalendar: "Room Booking Calendar",
  RoomDropdwn: "Room Dropdown",
  RoomImagesPage: "Room Images",
  RoomListPage: "Room List",
  RoomMaintenancePage: "Room Maintenance",
  RoomPricePage: "Room Price",
  RoomQRPage: "Room QR",
  RoomServiceRequestsPage: "Room Service Requests",
  RoomStatusPage: "Room Status",
  RoomTypesPage: "Room Types",
  SearchGuestPage: "Search Guest",
  SetupHotel: "Setup Hotel",
  SetupHotelPage: "Setup Hotel",
  Spinner: "Spinner",
  Staff: "Staff",
  StaffList: "Staff List",
  TasksPage: "Tasks",
  tesmp: "Tesmp",
  UserDashboard: "User Dashboard",
  UsersPage: "Users",
  SystemLogPage: "System Logs",
  CssEditorPage: "CSS Editor",
};

function ModuleAssignmentInner() {
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const { message, notification } = App.useApp();

  /* ───────── state ───────── */
  const { authData } = useAuth();
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [view, setView] = useState("general"); // 'general' | 'admin'
  const [search, setSearch] = useState("");

  /* ───────── grouping logic ───────── */
  const mergedModules = useMemo(() => {
    const merged = (modules || []).map((m) => {
      const a = (assignments || []).find((x) => x.moduleId === m.moduleId);
      return a ? { ...m, modulePermissionId: a.modulePermissionId } : m;
    });

    // If some modules have no modulePermissionId yet, keep them but they will render disabled.
    return merged;
  }, [modules, assignments]);

  const isSystemAdminUser = useMemo(
    () => authData?.role?.toLowerCase?.() === "systemadmin",
    [authData]
  );

  const generalModulesGroup = useMemo(
    () =>
      mergedModules.filter(
        (m) =>
          m.controllerName !== "SystemAdminController" &&
          m.controllerName !== "StylesController"
      ),
    [mergedModules]
  );

  const adminModulesGroup = useMemo(
    () =>
      mergedModules.filter(
        (m) =>
          m.controllerName === "SystemAdminController" ||
          m.controllerName === "StylesController"
      ),
    [mergedModules]
  );

  const filteredGeneral = useMemo(() => {
    if (!search) return generalModulesGroup;
    const s = search.toLowerCase();
    return generalModulesGroup.filter((m) =>
      (menuLabelMap[m.moduleName] || m.moduleName).toLowerCase().includes(s)
    );
  }, [generalModulesGroup, search]);

  const filteredAdmin = useMemo(() => {
    if (!search) return adminModulesGroup;
    const s = search.toLowerCase();
    return adminModulesGroup.filter((m) =>
      (menuLabelMap[m.moduleName] || m.moduleName).toLowerCase().includes(s)
    );
  }, [adminModulesGroup, search]);

  /* ───────── owner lock logic ───────── */
  const selectedRole = useMemo(() => {
    return roles.find((r) => r?.id?.toString?.() === roleId) || null;
  }, [roles, roleId]);

  const isOwnerRoleSelected = useMemo(() => {
    return (selectedRole?.name || "").toLowerCase() === "owner";
  }, [selectedRole]);

  // Lock ModuleAssignment for Owner so it can’t be unchecked/cleared
  const lockedPermissionIds = useMemo(() => {
    if (!isOwnerRoleSelected) return new Set();

    const ids = (mergedModules || [])
      .filter((m) => (m.moduleName || "").toLowerCase() === "moduleassignment")
      .map((m) => m.modulePermissionId)
      .filter(Boolean);

    return new Set(ids);
  }, [isOwnerRoleSelected, mergedModules]);

  // Ensure locked IDs always stay selected
  useEffect(() => {
    if (!lockedPermissionIds.size) return;

    setSelectedPermissionIds((prev) => {
      const next = new Set(prev || []);
      lockedPermissionIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [lockedPermissionIds]);

  /* ───────── counters ───────── */
  const totalModules = mergedModules.length;
  const totalGeneral = generalModulesGroup.length;
  const totalAdmin = adminModulesGroup.length;
  const totalSelected = selectedPermissionIds.length;
  const totalRoles = roles.length;

  /* ───────── data fetching ───────── */
  const fetchModules = useCallback(async () => {
    const { data } = await axiosInstance.get("/modules");
    setModules(data || []);
  }, []);

  const fetchAssignments = useCallback(async () => {
    const { data } = await axiosInstance.get("/modulepermissions");
    setAssignments(data || []);
  }, []);

  const fetchRoles = useCallback(async () => {
    if (!authData?.hotelId) {
      setRoles([]);
      return;
    }
    const { data } = await axiosInstance.get(`/roles/byhotel/${authData.hotelId}`);
    setRoles(data || []);
  }, [authData?.hotelId]);

  const fetchRoleAssignments = useCallback(
    async (rid) => {
      if (!rid) {
        setSelectedPermissionIds([]);
        return;
      }

      const { data } = await axiosInstance.get(`/modulepermissions/byrole/${rid}`);
      let ids = (data || []).map((r) => r.modulePermissionId).filter(Boolean);

      // Your existing logic: auto-include admin modules for Owner
      const sel = roles.find((r) => r?.id?.toString?.() === rid);
      if (sel?.name?.toLowerCase?.() === "owner") {
        const extra = adminModulesGroup.map((m) => m.modulePermissionId).filter(Boolean);
        ids = [...new Set([...ids, ...extra])];
      }

      // Re-assert locks (ModuleAssignment) for Owner
      if ((sel?.name || "").toLowerCase() === "owner" && lockedPermissionIds.size) {
        ids = [...new Set([...ids, ...Array.from(lockedPermissionIds)])];
      }

      setSelectedPermissionIds(ids);
    },
    [roles, adminModulesGroup, lockedPermissionIds]
  );

  useEffect(() => {
    (async () => {
      try {
        setBootLoading(true);
        setError(null);
        await Promise.all([fetchModules(), fetchRoles(), fetchAssignments()]);
      } catch (e) {
        console.error(e);
        setError("Failed to load data.");
      } finally {
        setBootLoading(false);
      }
    })();
  }, [fetchModules, fetchRoles, fetchAssignments]);

  useEffect(() => {
    if (!roles.length) return;
    // default to "User" if present
    const userRole = roles.find((r) => r?.name?.toLowerCase?.() === "user") || roles[0];
    if (userRole?.id) setRoleId(userRole.id.toString());
  }, [roles]);

  useEffect(() => {
    if (roleId) fetchRoleAssignments(roleId);
  }, [roleId, fetchRoleAssignments]);

  /* ───────── handlers ───────── */
  const onToggleGroup = (group, selectAll) => {
    const ids = group.map((m) => m.modulePermissionId).filter(Boolean);

    setSelectedPermissionIds((prev) => {
      const current = new Set(prev || []);

      if (selectAll) {
        ids.forEach((id) => current.add(id));
        lockedPermissionIds.forEach((id) => current.add(id));
        return Array.from(current);
      }

      // Clear: remove only non-locked items
      ids.forEach((id) => {
        if (!lockedPermissionIds.has(id)) current.delete(id);
      });

      // Re-assert locks
      lockedPermissionIds.forEach((id) => current.add(id));
      return Array.from(current);
    });
  };

  const onSubmit = async () => {
    if (!roleId) {
      message.warning("Please select a role first.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const finalIds = Array.from(
        new Set([...(selectedPermissionIds || []), ...Array.from(lockedPermissionIds)])
      );

      await axiosInstance.post("/modulepermissions/assign", {
        RoleId: roleId,
        ModuleIds: finalIds,
      });

      // Menu uses queryKey: ["modules", roleId, hotelId] — invalidate broadly
      queryClient.invalidateQueries({ queryKey: ["modules"] });

      hub.publish("permissionsChanged", { roleId, moduleIds: finalIds });

      notification.success({
        message: "Permissions Saved",
        description: "Role permissions have been updated and propagated to active sessions.",
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || "Error saving assignment.";
      setError(msg);
      notification.error({
        message: "Save Failed",
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const getModuleLabel = (name) => menuLabelMap[name] || name;

  /* ───────── render helpers ───────── */
  const renderModuleList = (items) => {
    if (bootLoading) return <Skeleton active paragraph={{ rows: 4 }} />;

    if (!items.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No modules match your search."
        />
      );
    }

    // Only modules with a modulePermissionId can be selected.
    const selectableIds = items.map((i) => i.modulePermissionId).filter(Boolean);

    return (
      <Checkbox.Group
        style={{ width: "100%" }}
        value={selectedPermissionIds}
        onChange={(vals) => {
          // Keep selections from other panels, but replace within this panel
          const others = (selectedPermissionIds || []).filter(
            (id) => !selectableIds.includes(id)
          );

          const nextWithinPanel = (vals || []).filter((v) => selectableIds.includes(v));

          const next = new Set([...others, ...nextWithinPanel]);
          lockedPermissionIds.forEach((id) => next.add(id));

          setSelectedPermissionIds(Array.from(next));
        }}
      >
        <Flex wrap="wrap" gap={8}>
          {items.map((m) => {
            const id = m.modulePermissionId;
            const locked = !!id && lockedPermissionIds.has(id);
            const disabled = !id || locked;

            const disabledReason = !id
              ? "This module has no permission record (modulePermissionId) yet."
              : locked
              ? "Required for Owner role. Cannot be removed."
              : undefined;

            return (
              <Card
                key={id ?? `${m.moduleId}-${m.moduleName}`}
                size="small"
                variant="outlined"
                hoverable={!disabled}
                style={{
                  width: screens.xs ? "100%" : 300,
                  opacity: disabled ? 0.65 : 1,
                }}
                styles={{ body: { padding: 12 } }}
              >
                <Space
                  align="start"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Tooltip title={disabled ? disabledReason : undefined}>
                    <Checkbox value={id} disabled={disabled}>
                      <Text>
                        {getModuleLabel(m.moduleName)}
                        {locked ? " (Required for Owner)" : ""}
                      </Text>
                    </Checkbox>
                  </Tooltip>
                </Space>
              </Card>
            );
          })}
        </Flex>
      </Checkbox.Group>
    );
  };

  const generalAllSelected =
    totalGeneral > 0 &&
    generalModulesGroup
      .map((m) => m.modulePermissionId)
      .filter(Boolean)
      .every((id) => selectedPermissionIds.includes(id));

  const adminAllSelected =
    totalAdmin > 0 &&
    adminModulesGroup
      .map((m) => m.modulePermissionId)
      .filter(Boolean)
      .every((id) => selectedPermissionIds.includes(id));

  /* ───────── UI ───────── */
  return (
    <div style={{ paddingInline: 12, paddingBottom: 24 }}>
      <Breadcrumb />

      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        wrap
        gap={12}
        style={{ marginTop: 8 }}
      >
        <Space align="center" wrap>
          <Title level={3} style={{ margin: 0 }}>
            Module Permissions
          </Title>
          <Tooltip title="What does this page do?">
            <Button
              type="text"
              icon={<InfoCircleOutlined />}
              onClick={() => setShowInfo(true)}
              aria-label="Module Assignment Info"
            />
          </Tooltip>
        </Space>

        <Space wrap>
          <Input
            allowClear
            placeholder="Search modules..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: screens.xs ? 220 : 280 }}
          />

          <Select
            style={{ width: screens.xs ? 220 : 260 }}
            placeholder="Select role"
            value={roleId || undefined}
            onChange={setRoleId}
            options={[
              { value: "", label: "Select Role", disabled: true },
              ...roles.map((r) => ({ value: r.id.toString(), label: r.name })),
            ]}
          />

          <Button icon={<PlusOutlined />} onClick={() => setShowRoleModal(true)}>
            Add Role
          </Button>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={onSubmit}
          >
            Save
          </Button>
        </Space>
      </Flex>

      <Divider />

      {/* Stats */}
      <Flex gap={12} wrap>
        <Card size="small" variant="outlined">
          <Statistic title="Total Modules" value={totalModules} prefix={<ApartmentOutlined />} />
        </Card>

        <Card size="small" variant="outlined">
          <Statistic
            title="Selected for Role"
            value={totalSelected}
            prefix={<SafetyCertificateOutlined />}
          />
        </Card>

        <Card size="small" variant="outlined">
          <Statistic title="General Modules" value={totalGeneral} />
        </Card>

        {isSystemAdminUser && (
          <Card size="small" variant="outlined">
            <Statistic title="Admin Modules" value={totalAdmin} />
          </Card>
        )}

        <Card size="small" variant="outlined">
          <Statistic title="Available Roles" value={totalRoles} />
        </Card>
      </Flex>

      {error && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          message="Operation Failed"
          description={error}
          showIcon
        />
      )}

      <Card style={{ marginTop: 16 }} variant="outlined" styles={{ body: { paddingTop: 8 } }}>
        {/* View toggle (helps on small screens) */}
        <Flex
          justify="space-between"
          align="center"
          wrap
          gap={8}
          style={{ marginBottom: 8 }}
        >
          <Segmented
            value={view}
            onChange={setView}
            options={[
              {
                label: (
                  <>
                    General <Badge count={totalGeneral} size="small" />
                  </>
                ),
                value: "general",
              },
              ...(isSystemAdminUser
                ? [
                    {
                      label: (
                        <>
                          Administration <Badge count={totalAdmin} size="small" />
                        </>
                      ),
                      value: "admin",
                    },
                  ]
                : []),
            ]}
          />

          <Space>
            {view === "general" && (
              <>
                <Button
                  icon={<CheckSquareOutlined />}
                  onClick={() => onToggleGroup(generalModulesGroup, true)}
                  disabled={!generalModulesGroup.length}
                >
                  Select All
                </Button>
                <Button
                  icon={<CloseSquareOutlined />}
                  onClick={() => onToggleGroup(generalModulesGroup, false)}
                  disabled={!generalModulesGroup.length}
                >
                  Clear
                </Button>
                <Badge
                  status={generalAllSelected ? "success" : "default"}
                  text={generalAllSelected ? "All selected" : "Partial"}
                />
              </>
            )}

            {view === "admin" && (
              <>
                <Button
                  icon={<CheckSquareOutlined />}
                  onClick={() => onToggleGroup(adminModulesGroup, true)}
                  disabled={!adminModulesGroup.length}
                >
                  Select All
                </Button>
                <Button
                  icon={<CloseSquareOutlined />}
                  onClick={() => onToggleGroup(adminModulesGroup, false)}
                  disabled={!adminModulesGroup.length}
                >
                  Clear
                </Button>
                <Badge
                  status={adminAllSelected ? "success" : "default"}
                  text={adminAllSelected ? "All selected" : "Partial"}
                />
              </>
            )}
          </Space>
        </Flex>

        {/* Panels */}
        <Collapse
          bordered={false}
          defaultActiveKey={["panel"]}
          items={[
            {
              key: "panel",
              label: view === "general" ? "General Modules" : "Administration Modules",
              extra: (
                <Text type="secondary">
                  {search ? "Filtered" : "All"} ·{" "}
                  <Badge count={view === "general" ? filteredGeneral.length : filteredAdmin.length} />
                </Text>
              ),
              children: (
                <div style={{ paddingBlock: 8 }}>
                  {view === "general" ? (
                    renderModuleList(filteredGeneral)
                  ) : isSystemAdminUser ? (
                    renderModuleList(filteredAdmin)
                  ) : (
                    <Alert
                      type="info"
                      message="Administration modules are visible only to System Admin users."
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Role modal */}
      <RoleCreationModal
        open={showRoleModal}
        onCancel={() => setShowRoleModal(false)}
        onRoleCreated={(newRole) => {
          setRoles((prev) => [...prev, newRole]);
          setRoleId(newRole?.id?.toString?.() || "");
        }}
        hotelId={authData?.hotelId}
      />

      {/* Info Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Module Assignment – Guide</Text>
          </Space>
        }
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Close
          </Button>,
        ]}
        width={720}
      >
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Text>
            Assign or revoke access to application modules for the selected role. Your changes
            propagate to open sessions via the event hub and react-query cache invalidation.
          </Text>

          <Divider style={{ margin: "8px 0" }} />

          <Title level={5} style={{ margin: 0 }}>
            Quick Steps
          </Title>
          <ol style={{ paddingLeft: 18, margin: 0 }}>
            <li>
              Select a <strong>Role</strong> (or create a new one).
            </li>
            <li>
              Use <strong>Select All</strong> or tick individual modules.
            </li>
            <li>
              Click <strong>Save</strong>.
            </li>
          </ol>

          <Title level={5} style={{ margin: 0 }}>
            Notes & Rules
          </Title>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            <li>
              Admin modules are visible only to System Admin users. Owners may be auto-granted admin
              modules per your logic.
            </li>
            <li>
              On save we publish <code>permissionsChanged</code> and invalidate{" "}
              <code>["modules"]</code> queries so menus refresh.
            </li>
            <li>Use the search box to quickly filter modules by name.</li>
            <li>
              For Owner role, <strong>Module Assignment</strong> is required and cannot be removed.
            </li>
          </ul>

          <Title level={5} style={{ margin: 0 }}>
            Current Status
          </Title>
          <Space size="large" wrap>
            <Space>
              <Text>Total Modules:</Text>
              <Badge count={totalModules} showZero />
            </Space>
            <Space>
              <Text>Selected for Role:</Text>
              <Badge count={totalSelected} showZero />
            </Space>
            <Space>
              <Text>General Modules:</Text>
              <Badge count={totalGeneral} showZero />
            </Space>
            {isSystemAdminUser && (
              <Space>
                <Text>Admin Modules:</Text>
                <Badge count={totalAdmin} showZero />
              </Space>
            )}
            <Space>
              <Text>Available Roles:</Text>
              <Badge count={totalRoles} showZero />
            </Space>
          </Space>
        </Space>
      </Modal>
    </div>
  );
}

export default function ModuleAssignment() {
  // Ensures App context exists for App.useApp() (message/notification/modal) in AntD v6.
  return (
    <App>
      <ModuleAssignmentInner />
    </App>
  );
}
